import axios, { AxiosError, AxiosRequestConfig } from 'axios';
// uncomment to debug by printing axios requests as curl commands
// import curler from 'axios-curlirize';
import { applicationId, nativeBuildVersion } from 'expo-application';
import { Platform } from 'react-native';

import config from '@lib/config';
import { Version } from '@lib/constants/Version';
import { Auth } from '@lib/entities';
import { readE2eSkipKeys } from '@lib/utils/e2eSkipKeys';
import Storage from '@lib/utils/storage';

/**
 * @deprecated, use declarativeClient / ApiClientError
 */
export interface ApiError {
    error: string;
    statusCode?: number;
}

export function isApiError(data: unknown): data is ApiError {
    return (data as ApiError)?.error !== undefined;
}

/**
 * `/phone/start` 409 — the submitted number already belongs to another VERIFIED account.
 *
 * Distinct from every other AV error because the caller does not toast a message at all: it
 * opens a dedicated modal that names the account owning the number, so the masked email has
 * to survive `AuthService`'s error mapping (which collapses all other statuses to a bare
 * `{ error }`). Callers detect it by reading `statusCode === 409` directly.
 *
 * Response body (409):
 *   { success: false, errorMessage: "An account already exists with this phone number.",
 *     masked_email: "j***1@justsalad.com", ... }
 */
export type PhoneInUseError = ApiError & {
    statusCode: 409;
    /** Obfuscated owner address, e.g. `j***1@justsalad.com`. Absent if the backend omits it. */
    maskedEmail?: string;
};

export function handleAxiosError(
    error: AxiosError,
    noInternetMessage = 'No internet connection.'
): ApiError {
    if (error.message === 'Network Error') {
        return { error: noInternetMessage };
    } else {
        return {
            error: 'An unknown error has occurred.',
            statusCode: error.response?.status
        };
    }
}

const baseURL = config.apiBaseUrl;

const client = axios.create({
    baseURL
});

// Configure axios-retry for the specific client instance
/*
axiosRetry(client, {
    retries: 5,
    retryDelay: (retryCount) => {
        return Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s, 8s, etc.
    },
    onRetry: (retryCount, error, requestConfig) => {
        console.warn(`Retry attempt ${retryCount} for ${requestConfig.url}:`, error.message);
    },
    // Specify conditions to retry on, this is the default
    // which will retry on network errors or idempotent requests (5xx)
    retryCondition: (error) => {
        const shouldRetry = axiosRetry.isNetworkOrIdempotentRequestError(error);
        return shouldRetry;
    }
});*/

// don't send bearer tokens to non-authenticated aroutes. In particular the
// refresh token route is fragile and won't work with a bearer.
const BearerTokenBlacklist = [
    'v1/Security/Login',
    'v4/Security/Login',
    'v5/Security/Login',
    'v1/Security/RefreshToken',
    'v1/Security/impersonate/exchange',
    'v1/Security/impersonate/exchange-jwt',
    'v1/Security/social/exchange',
    'v2/Security/signup',
    'v3/user/signup',
    'v5/Security/signup',
    'v5/Security/verify-mfa',
    'v5/Security/resend-mfa',
    'v5/Security/phone/start',
    'v5/Security/phone/verify',
    'v5/Security/phone/resend',
    'v5/Security/phone/skip'
].map((url) => url.toLowerCase());

const isAuthenticatedUrl = (url: string) => {
    return !BearerTokenBlacklist.includes(url.toLowerCase());
};

// uncomment to debug
//
// curler(client as any, (r, e) => {
//     console.error(e);
//     console.error(r.command);
// })

type URLSafeString = string & '__urlsafe';

export const toUrlParams = (params: Record<string, string | number>): URLSafeString => {
    const safe = new URLSearchParams();

    for (const key in params) {
        safe.append(key, params[key].toString());
    }

    return safe.toString() as URLSafeString;
};

export const wrapApiCall =
    <T, U>(call: (...params: T[]) => Promise<U>) =>
    async (...args: T[]): Promise<U | ApiError> => {
        try {
            const response = await call(...args);
            return response;
        } catch (error: unknown | AxiosError) {
            if (axios.isAxiosError(error) && error) {
                if (error.response?.data?.error) {
                    return error.response?.data as ApiError;
                }
                return handleAxiosError(error);
            }
            throw error;
        }
    };

const tokenRefreshPath = 'v1/security/RefreshToken';

// Unique key per id so different environments don't collide
const AUTH_STORE_KEY = `JS_AUTH_${applicationId ?? 'WEB'}`;

export const getStoredSession = async () => {
    const authDataString = await Storage.getItemAsync(AUTH_STORE_KEY);
    if (authDataString) {
        return JSON.parse(authDataString) as Auth;
    }
};

export const setStoredSession = async (auth: Auth) => {
    await Storage.setItemAsync(AUTH_STORE_KEY, JSON.stringify(auth));
};

export const clearSession = async () => {
    await Storage.deleteItemAsync(AUTH_STORE_KEY);
};

// `nativeBuildVersion` is undefined for web
// NEXT_PUBLIC_SENTRY_RELEASE is the commit SHA of the release
const buildNumber = `${Platform.OS}/${
    nativeBuildVersion ?? process.env.NEXT_PUBLIC_SENTRY_RELEASE
}`;

let clientIp: string | undefined = undefined;

export const setClientIp = (ip: string | undefined) => {
    clientIp = ip;
};

// Add Bearer token to requests when access token exists
// Add build number to all requests
// Add client version to all requests
client.interceptors.request.use(
    async (axiosConfig) => {
        const session = await getStoredSession();
        const token = session?.authToken?.token;

        // If this is a non auth route attach Bearer token
        if (token && isAuthenticatedUrl(axiosConfig.url || '')) {
            axiosConfig.headers.set('Authorization', `Bearer ${token}`);
        }

        axiosConfig.headers.set('X-Client-Build-Number', buildNumber);
        axiosConfig.headers.set('X-Client-Version', Version ?? '1.0.0');

        if (clientIp) {
            axiosConfig.headers.set('X-JustSalad-Client-IP', clientIp);
        }

        // If we are running E2E tests, add header to bypass hCaptcha
        if (config.e2eTesting && config.justSaladSecretKey) {
            axiosConfig.headers.set('justsalad-secret-key', config.justSaladSecretKey);
        }

        if (config.e2eTesting && config.automatedTestKey) {
            axiosConfig.headers.set('automated-test', config.automatedTestKey || '');
        }

        // E2E-only lever: the test runner writes the backend's skip keys into
        // storage before the page loads; the app only forwards them (see
        // lib/utils/e2eSkipKeys.ts).
        const e2eSkipKeys = readE2eSkipKeys();
        if (e2eSkipKeys?.secretKey) {
            axiosConfig.headers.set('justsalad-secret-key', e2eSkipKeys.secretKey);
        }
        if (e2eSkipKeys?.automatedTestKey) {
            axiosConfig.headers.set('automated-test', e2eSkipKeys.automatedTestKey);
        }
        if (e2eSkipKeys?.e2eKey) {
            axiosConfig.headers.set('X-E2E-Key', e2eSkipKeys.e2eKey);
        }

        return axiosConfig;
    },
    (error) => {
        Promise.reject(error);
    }
);

let isRefreshing = false;
client.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => {
        const requestUrl = error.config.url.toLowerCase();
        const status = error.response?.status; // server sends empties sometimes

        // 428 = MFA required. Return the response body (includes authToken + requiresMfa)
        // instead of treating it as an error — the login/signup/wallet/process-payment gates
        // read that body.
        //
        // The v6 quote endpoint reuses 428 for a different meaning: an unverified account
        // tried to apply a promo code. That is NOT a challenge to launch — it's a plain
        // rejection that getQuote maps to a "verify your account" toast. So let the quote 428
        // fall through to the normal reject path below; every other endpoint is unaffected.
        const isQuote428 = requestUrl.endsWith('/checkout/quote');
        if (status === 428 && error.response?.data && !isQuote428) {
            return error.response;
        }

        // Only session-bearer routes get the refresh-and-retry treatment. Routes on the
        // BearerTokenBlacklist (login, signup, verify-mfa, resend-mfa, phone/*) carry a
        // purpose-specific token (or none), so a 401 there means THAT token failed — we
        // must surface it to the caller, not silently swap in the stored-session token
        // and retry (which would validate under the wrong identity / mask real failures).
        const isPossiblyTokenExpiryError = status === 401 && isAuthenticatedUrl(requestUrl);

        if (!isPossiblyTokenExpiryError) {
            return Promise.reject(error);
        }

        const originalRequest = error.config;

        // if this error comes from a refresh request, cancel
        // everything and bail
        if (originalRequest.url === tokenRefreshPath) {
            await clearSession();
            return Promise.reject(error);
        }
        // if this request is already a retry, bail
        if (originalRequest._retry) {
            return Promise.reject(error);
        }

        // mark this as a retry so we don't get stuck in a recurse loop
        originalRequest._retry = true;

        const session = await getStoredSession();
        const refreshToken = session?.authToken.refreshToken;

        // if there isn't a refresh token, bail and clear
        if (!refreshToken) {
            return Promise.reject(error);
        }

        // if we're currently sending a refresh request, wait for it to finish
        if (isRefreshing) {
            let flood = 100; // ten second timeout
            return new Promise((resolve, reject) => {
                const timer = setInterval(() => {
                    if (flood < 0) {
                        clearInterval(timer);
                        reject(error);
                    }

                    if (isRefreshing) {
                        flood--;
                        return;
                    }

                    clearInterval(timer);
                    reauthorize(originalRequest).then(resolve);
                }, 100);
            });
        }

        const refreshedSession = await refresh(refreshToken);

        if (isApiError(refreshedSession)) {
            return Promise.reject(error);
        }

        await setStoredSession(refreshedSession);

        return reauthorize(originalRequest);
    }
);

const reauthorize = async (request: AxiosRequestConfig): Promise<AxiosRequestConfig> => {
    const session = await getStoredSession();
    if (!session) {
        return request;
    }

    request.headers = {
        ...request.headers,
        Authorization: `Bearer ${session.authToken.token}`
    };

    return client(request);
};

export const refresh = async (refreshToken: string): Promise<Auth | ApiError> => {
    isRefreshing = true;
    try {
        const data = await client.post(tokenRefreshPath, {
            RefreshToken: refreshToken
        });

        return data.data;
    } catch (error: unknown | AxiosError) {
        if (axios.isAxiosError(error) && error) {
            if (error.response?.data?.error) {
                return error.response?.data as ApiError;
            }
            return handleAxiosError(error);
        }
        throw error;
    } finally {
        isRefreshing = false;
    }
};

export default client;
